import { useCallback, useEffect, useMemo, useState } from "react";
import { dataApi, api, authedUrl } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Primitives";
import DataTable from "@/components/DataTable";
import EmployeeFormDialog from "@/components/EmployeeFormDialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Eye, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { BranchTag, useBranches } from "@/components/BranchBits";

export default function Employees() {
  const { user } = useAuth();
  const canCreate = user?.role === "admin" || user?.role === "branch_admin";
  const { byId: branchById } = useBranches();
  const [emps, setEmps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dept, setDept] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const nav = useNavigate();

  const load = () => {
    setLoading(true);
    dataApi.employees().then((r) => setEmps(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const departments = useMemo(() => Array.from(new Set(emps.map((e) => e.department))).sort(), [emps]);
  const rows = emps.filter((e) => dept === "all" || e.department === dept);

  const deactivate = async (id) => {
    if (!window.confirm("Deactivate this employee?")) return;
    try { await api.delete(`/employees/${id}`); toast.success("Deactivated"); load(); }
    catch { toast.error("Failed"); }
  };

  const rowActions = useCallback((r) => (
    <div className="flex justify-end gap-1">
      <Button data-testid={`view-emp-${r.id}`} variant="ghost" size="icon" onClick={() => nav(`/employees/${r.id}`)}><Eye className="w-4 h-4" /></Button>
      {r.can_modify && <Button data-testid={`edit-emp-${r.id}`} variant="ghost" size="icon" onClick={() => { setEditing(r); setDialogOpen(true); }}><Pencil className="w-4 h-4" /></Button>}
      {r.can_modify && <Button data-testid={`del-emp-${r.id}`} variant="ghost" size="icon" onClick={() => deactivate(r.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>}
    </div>
  ), [nav]);

  const columns = [
    { key: "full_name", label: "Employee", sortable: true, render: (r) => (
      <div className="flex items-center gap-2">
        <Avatar className="h-7 w-7"><AvatarImage src={authedUrl(r.avatar_url)} /><AvatarFallback className="text-[9px]">{r.full_name?.split(" ").map(s => s[0]).slice(0,2).join("")}</AvatarFallback></Avatar>
        <div>
          <div className="font-medium">{r.full_name}</div>
          <div className="text-[10px] text-muted-foreground">{r.email}</div>
        </div>
      </div>
    )},
    { key: "emp_code", label: "EMP ID", sortable: true, render: (r) => <span className="font-mono text-xs">{r.emp_code || "—"}</span> },
    { key: "designation", label: "Designation", sortable: true },
    { key: "department", label: "Department", sortable: true },
    { key: "employment_type", label: "Type", sortable: true, render: (r) => <Badge variant="outline" className="capitalize text-[10px]">{(r.employment_type || "").replace(/_/g, " ")}</Badge> },
    { key: "productivity_score", label: "Productivity", sortable: true, className: "number", render: (r) => `${r.productivity_score}%` },
    { key: "attendance_score", label: "Attendance", sortable: true, className: "number", render: (r) => `${r.attendance_score}%` },
    { key: "utilization", label: "Utilization", sortable: true, render: (r) => (
      <span className={r.utilization > 90 ? "text-red-500" : r.utilization < 60 ? "text-amber-500" : ""}>{r.utilization}%</span>
    )},
    { key: "branch_id", label: "Branch", sortable: true, render: (r) => <BranchTag id={r.branch_id} byId={branchById} /> },
    { key: "role", label: "Role", sortable: true, render: (r) => <Badge variant="outline" className="capitalize text-[10px]">{r.role}</Badge> },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        testid="employees-header" title="Employees" subtitle={`${emps.length} team members`}
        actions={canCreate && <Button data-testid="new-emp-btn" onClick={() => { setEditing(null); setDialogOpen(true); }}><Plus className="w-4 h-4 mr-1" />New Employee</Button>}
      />
      <DataTable
        testid="dt-emp"
        loading={loading}
        empty="No employees in your scope"
        columns={columns}
        data={rows}
        searchKeys={["full_name", "email", "department", "designation", "emp_code"]}
        rowActions={rowActions}
        extraFilters={
          <Select value={dept} onValueChange={setDept}>
            <SelectTrigger className="w-40 h-9" data-testid="emp-dept-filter"><SelectValue placeholder="Department" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All departments</SelectItem>
              {departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
        }
      />
      <EmployeeFormDialog open={dialogOpen} onOpenChange={setDialogOpen} employee={editing} onSaved={load} />
    </div>
  );
}
